import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Create a theme instance with a modern color palette
let theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb', // Modern blue
      light: '#60a5fa',
      dark: '#1e40af',
      primary: "#172E7A",
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#FF9400', // Modern green
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#b91c1c',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      disabled: '#9ca3af',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.1)',
    '0px 1px 5px rgba(0, 0, 0, 0.05), 0px 1px 8px rgba(0, 0, 0, 0.1)',
    '0px 2px 4px rgba(0, 0, 0, 0.05), 0px 3px 8px rgba(0, 0, 0, 0.1)',
    '0px 3px 5px rgba(0, 0, 0, 0.04), 0px 4px 12px rgba(0, 0, 0, 0.08)',
    '0px 4px 6px rgba(0, 0, 0, 0.04), 0px 5px 15px rgba(0, 0, 0, 0.08)',
    '0px 5px 8px rgba(0, 0, 0, 0.04), 0px 6px 18px rgba(0, 0, 0, 0.08)',
    '0px 6px 10px rgba(0, 0, 0, 0.04), 0px 8px 22px rgba(0, 0, 0, 0.08)',
    '0px 7px 12px rgba(0, 0, 0, 0.04), 0px 9px 25px rgba(0, 0, 0, 0.08)',
    '0px 8px 14px rgba(0, 0, 0, 0.04), 0px 10px 29px rgba(0, 0, 0, 0.08)',
    '0px 9px 16px rgba(0, 0, 0, 0.04), 0px 12px 32px rgba(0, 0, 0, 0.08)',
    '0px 10px 18px rgba(0, 0, 0, 0.04), 0px 14px 36px rgba(0, 0, 0, 0.08)',
    '0px 11px 20px rgba(0, 0, 0, 0.04), 0px 16px 40px rgba(0, 0, 0, 0.08)',
    '0px 12px 22px rgba(0, 0, 0, 0.04), 0px 18px 45px rgba(0, 0, 0, 0.08)',
    '0px 13px 24px rgba(0, 0, 0, 0.04), 0px 20px 50px rgba(0, 0, 0, 0.08)',
    '0px 14px 26px rgba(0, 0, 0, 0.04), 0px 22px 55px rgba(0, 0, 0, 0.08)',
    '0px 15px 28px rgba(0, 0, 0, 0.04), 0px 24px 60px rgba(0, 0, 0, 0.08)',
    '0px 16px 30px rgba(0, 0, 0, 0.04), 0px 26px 65px rgba(0, 0, 0, 0.08)',
    '0px 17px 32px rgba(0, 0, 0, 0.04), 0px 28px 70px rgba(0, 0, 0, 0.08)',
    '0px 18px 34px rgba(0, 0, 0, 0.04), 0px 30px 75px rgba(0, 0, 0, 0.08)',
    '0px 19px 36px rgba(0, 0, 0, 0.04), 0px 32px 80px rgba(0, 0, 0, 0.08)',
    '0px 20px 38px rgba(0, 0, 0, 0.04), 0px 34px 85px rgba(0, 0, 0, 0.08)',
    '0px 21px 40px rgba(0, 0, 0, 0.04), 0px 36px 90px rgba(0, 0, 0, 0.08)',
    '0px 22px 42px rgba(0, 0, 0, 0.04), 0px 38px 95px rgba(0, 0, 0, 0.08)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.04), 0px 5px 15px rgba(0, 0, 0, 0.08)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05), 0px 3px 8px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 6px 10px rgba(0, 0, 0, 0.04), 0px 8px 22px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05), 0px 3px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        },
        head: {
          fontWeight: 600,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0px 8px 14px rgba(0, 0, 0, 0.04), 0px 10px 29px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});

// Make typography responsive
theme = responsiveFontSizes(theme);

export default theme;
